import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
    FaCalendarAlt,
    FaClock,
    FaVideo,
    FaPlayCircle,
    FaExternalLinkAlt,
    FaExclamationTriangle,
    FaFileAlt,
    FaRegClock,
    FaChevronDown,
    FaChevronRight,
    FaBars,
    FaTimes,
    FaList,
    FaBookOpen,
    FaHome,
    FaTachometerAlt,
    FaChartBar,
    FaBriefcase,
    FaDollarSign,
    FaBell,
    FaCompass,
    FaCog,
    FaUsers,
    FaUserEdit,
    FaUserPlus,
    FaBuilding,
    FaAddressBook,
    FaTrash,
    FaCogs,
    FaShip,
    FaClipboardList,
    FaTags,
    FaPlus,
    FaMoneyBill,
    FaFolder,
    FaGavel,
    FaTasks,
    FaFileMedical,
    FaFileInvoice,
    FaFileInvoiceDollar,
    FaSearch,
    FaFilter,
    FaCalculator,
    FaLayerGroup,
    FaInfoCircle,
    FaFolderOpen,
    FaTimesCircle
} from 'react-icons/fa';
import { MdOutlineDescription, MdOutlineUpdate, MdVideoLibrary } from 'react-icons/md';
import './manualPage.css';

const api = axios.create({
    baseURL: 'https://uk.backend.hms.hiradentallab.com/api',
    headers: { 'Content-Type': 'application/json' }
});

const ManualsPage = () => {
    const [manual, setManual] = useState(null);
    const [loading, setLoading] = useState(true);
    const [videoErrors, setVideoErrors] = useState({});
    const [error, setError] = useState(null);
    const contentRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const videosSectionRef = useRef(null);
    const hasMarkedAsViewed = useRef(false);
    const hasFilteredInitially = useRef(false);

    // Sidebar state
    const [activeHeading, setActiveHeading] = useState(null); // Start with null to prevent auto-filtering
    const [expandedHeadings, setExpandedHeadings] = useState({
        'training-workflow': true // Keep training workflow expanded by default
    });
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // New state for scroll gradient indicators
    const [showTopGradient, setShowTopGradient] = useState(false);
    const [showBottomGradient, setShowBottomGradient] = useState(false);

    // Folder management states for video display
    const [videoFolders, setVideoFolders] = useState([]);
    const [expandedVideoFolders, setExpandedVideoFolders] = useState({});
    const [activeVideoFolder, setActiveVideoFolder] = useState('all');

    // Modal state for folder videos
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);

    // Define the headings structure for this specific manual
    const headingsStructure = [
        {
            id: 'purpose',
            text: 'Purpose',
            icon: <FaBookOpen />,
            level: 'h2',
            color: '#4A90D9'
        },
        {
            id: 'manual-videos',
            text: 'Manual & Videos',
            icon: <MdOutlineDescription />,
            level: 'h2',
            color: '#5B9BD5'
        },
        {
            id: 'training-workflow',
            text: 'Training Workflow',
            icon: <FaVideo />,
            level: 'h2',
            color: '#E67E22',
            children: [
                { id: 'retainer-whitening', text: 'Retainer / Whitening Tray Model', icon: <FaList />, level: 'h3', color: '#E88D3F' },
                { id: 'waxup-design', text: 'Wax-Up Design', icon: <FaList />, level: 'h3', color: '#E88D3F' },
                { id: 'zirconia-crown', text: 'Zirconia Crown', icon: <FaList />, level: 'h3', color: '#E88D3F' },
                { id: 'implant-crown', text: 'Implant Crown', icon: <FaList />, level: 'h3', color: '#E88D3F' },
                { id: 'zirconia-bridge', text: 'Zirconia Bridge', icon: <FaList />, level: 'h3', color: '#E88D3F' },
                { id: 'implant-bridge', text: 'Implant Bridge', icon: <FaList />, level: 'h3', color: '#E88D3F' },
                { id: 'full-arch-design', text: 'Full-Arch Design', icon: <FaList />, level: 'h3', color: '#E88D3F' }
            ]
        },
        {
            id: 'time-management',
            text: 'Time Management & Error Reduction',
            icon: <FaClock />,
            level: 'h2',
            color: '#27AE60'
        },
        {
            id: 'standardised-workflow',
            text: 'Standardised Workflow',
            icon: <FaLayerGroup />,
            level: 'h2',
            color: '#8E44AD'
        },
        {
            id: 'our-goal',
            text: 'Our Goal',
            icon: <FaTachometerAlt />,
            level: 'h2',
            color: '#E74C3C'
        },
        {
            id: 'training-videos',
            text: 'Training Videos',
            icon: <FaVideo />,
            level: 'h2',
            color: '#3498DB'
        }
    ];

    // Fetch manual and videos data
    useEffect(() => {
        fetchManual();
    }, []);

    // Handle scroll gradient indicators
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            setShowTopGradient(scrollTop > 10);
            setShowBottomGradient(scrollTop + clientHeight < scrollHeight - 10);
        };

        handleScroll();
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [manual?.content]);

    // Map heading IDs to search terms used to locate the matching heading in the HTML content
    const searchTermsMap = {
        'purpose': 'purpose',
        'manual-videos': 'manual & videos',
        'training-workflow': 'training workflow',
        'retainer-whitening': 'retainer / whitening tray model',
        'waxup-design': 'wax-up design',
        'zirconia-crown': 'zirconia crown',
        'implant-crown': 'implant crown',
        'zirconia-bridge': 'zirconia bridge',
        'implant-bridge': 'implant bridge',
        'full-arch-design': 'full-arch design',
        'time-management': 'time management & error reduction',
        'standardised-workflow': 'standardised workflow',
        'our-goal': 'our goal'
    };

    // Function to filter content based on active heading
    // FIX: previously this walked `contentRef.current.children`, but contentRef
    // wraps a single child (`.mp-tiptap-content`, the dangerouslySetInnerHTML div).
    // That meant the real headings/paragraphs (children of `.mp-tiptap-content`)
    // were never found in the top-level loop, so nothing after the heading itself
    // ever got shown again -> the whole documentation appeared to disappear.
    // Now we operate directly on `.mp-tiptap-content`'s direct children.
    const filterContentByHeading = useCallback((headingId) => {
        if (!contentRef.current) return;

        const wrapper = contentRef.current.querySelector('.mp-tiptap-content');
        if (!wrapper) return;

        const topLevelNodes = Array.from(wrapper.children);

        // Hide every top-level block first (their children hide automatically via CSS cascade)
        topLevelNodes.forEach(node => {
            node.style.display = 'none';
        });

        // Special handling for Training Videos section
        if (headingId === 'training-videos') {
            if (videosSectionRef.current) {
                videosSectionRef.current.style.display = 'block';
                setTimeout(() => {
                    videosSectionRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }, 100);
            }
            return;
        }

        // Hide videos section when not on training videos
        if (videosSectionRef.current) {
            videosSectionRef.current.style.display = 'none';
        }

        const searchTerm = searchTermsMap[headingId];
        if (!searchTerm) return;

        // Find the target heading anywhere inside the content
        const allHeadings = wrapper.querySelectorAll('h1, h2, h3, h4, h5, h6');
        let targetHeading = null;

        for (let i = 0; i < allHeadings.length; i++) {
            const heading = allHeadings[i];
            const headingText = heading.textContent.trim().toLowerCase();
            if (headingText.includes(searchTerm)) {
                targetHeading = heading;
                break;
            }
        }

        if (!targetHeading) return;

        // Walk up to find the top-level node (direct child of wrapper) that contains this heading
        let targetTopLevel = targetHeading;
        while (targetTopLevel.parentElement && targetTopLevel.parentElement !== wrapper) {
            targetTopLevel = targetTopLevel.parentElement;
        }

        const targetIndex = topLevelNodes.indexOf(targetTopLevel);
        if (targetIndex === -1) return;

        const targetLevel = parseInt(targetHeading.tagName.substring(1), 10);

        // Find where the next heading of the same or higher level starts (among top-level nodes)
        let endIndex = topLevelNodes.length;
        for (let i = targetIndex + 1; i < topLevelNodes.length; i++) {
            const node = topLevelNodes[i];
            const tag = node.tagName ? node.tagName.toLowerCase() : '';
            if (/^h[1-6]$/.test(tag)) {
                const level = parseInt(tag.substring(1), 10);
                if (level <= targetLevel) {
                    endIndex = i;
                    break;
                }
            }
        }

        // Show and style every top-level node between targetIndex and endIndex
        for (let i = targetIndex; i < endIndex; i++) {
            const node = topLevelNodes[i];
            node.style.display = 'block';

            const tag = node.tagName;

            if (/^H[1-6]$/.test(tag)) {
                node.style.textAlign = 'left';
                node.style.fontSize = headingId === 'purpose' ? '36px' : '30px';
                node.style.fontWeight = 'bold';
                node.style.margin = '40px 0 20px 0';
                node.style.color = '#1a202c';
            } else if (tag === 'P') {
                node.style.textAlign = 'left';
                node.style.color = '#64748b';
                node.style.fontSize = '16px';
                node.style.lineHeight = '1.6';
                node.style.marginBottom = '16px';
            } else if (tag === 'UL' || tag === 'OL') {
                node.style.textAlign = 'left';
                node.style.color = '#64748b';
                node.style.fontSize = '16px';
                node.style.lineHeight = '1.6';
                node.style.marginBottom = '16px';
                node.style.paddingLeft = '24px';

                // Style list items too, since they're nested one level deeper
                Array.from(node.children).forEach(li => {
                    if (li.tagName === 'LI') {
                        li.style.textAlign = 'left';
                        li.style.color = '#64748b';
                        li.style.fontSize = '16px';
                        li.style.lineHeight = '1.6';
                        li.style.marginBottom = '8px';
                    }
                });
            }
        }

        // Scroll to the target heading and briefly highlight it
        setTimeout(() => {
            if (scrollContainerRef.current && targetTopLevel) {
                const scrollContainer = scrollContainerRef.current;
                const targetRect = targetTopLevel.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                const scrollTop = targetRect.top - containerRect.top + scrollContainer.scrollTop - 20;

                scrollContainer.scrollTo({
                    top: scrollTop,
                    behavior: 'smooth'
                });

                targetTopLevel.style.backgroundColor = '#F0F4FF';
                targetTopLevel.style.transition = 'background-color 0.3s ease';
                targetTopLevel.style.borderRadius = '6px';
                targetTopLevel.style.padding = '8px 12px';

                setTimeout(() => {
                    targetTopLevel.style.backgroundColor = 'transparent';
                }, 2000);
            }
        }, 100);
    }, [manual?.content]);

    const fetchManual = async () => {
        setLoading(true);
        setError(null);

        try {
            console.log('Fetching manual data...');
            const response = await api.get('/manuals');
            console.log('Manual API Response:', response.data);

            if (response.data.success) {
                const manualData = response.data.data;
                console.log('Manual Data:', manualData);
                console.log('Manual Content:', manualData?.content);
                console.log('Manual Videos:', manualData?.videos);
                console.log('Manual Video Folders:', manualData?.videoFolders);

                setManual(manualData);

                // Set video folders and initialize expanded state
                const folders = manualData.videoFolders || [];
                console.log('Video Folders:', folders);
                setVideoFolders(folders);

                const expandedState = {};
                folders.forEach(folder => {
                    expandedState[folder._id] = true;
                });
                setExpandedVideoFolders(expandedState);

                if (!hasMarkedAsViewed.current) {
                    localStorage.setItem('manualLastViewedVersion', manualData.version);
                    hasMarkedAsViewed.current = true;
                }

                // Don't auto-filter on initial load - show all content
                hasFilteredInitially.current = true;
            } else {
                console.error('API returned success: false', response.data);
                setError('Failed to fetch manual: API returned unsuccessful response');
            }
        } catch (error) {
            console.error('Error fetching manual:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config
            });
            setError(`Error fetching manual: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const toggleHeading = (headingId) => {
        setExpandedHeadings(prev => ({
            ...prev,
            [headingId]: !prev[headingId]
        }));
    };

    const toggleVideoFolder = (folderId) => {
        setExpandedVideoFolders(prev => ({
            ...prev,
            [folderId]: !prev[folderId]
        }));
    };

    const findParentIds = (items, targetId, parents = []) => {
        for (let item of items) {
            if (item.id === targetId) return parents;
            if (item.children) {
                const result = findParentIds(item.children, targetId, [...parents, item.id]);
                if (result) return result;
            }
        }
        return null;
    };

    const findHeadingInfo = (items, id) => {
        for (let item of items) {
            if (item.id === id) return item;
            if (item.children) {
                const found = findHeadingInfo(item.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const scrollToSection = (headingId) => {
        const headingInfo = findHeadingInfo(headingsStructure, headingId);
        if (!headingInfo) return;

        setActiveHeading(headingId);

        const parentIds = findParentIds(headingsStructure, headingId);
        if (parentIds) {
            setExpandedHeadings(prev => {
                const newExpanded = { ...prev };
                parentIds.forEach(id => { newExpanded[id] = true; });
                return newExpanded;
            });
        }

        // Use the filter function
        filterContentByHeading(headingId);

        if (window.innerWidth <= 768) {
            setMobileSidebarOpen(false);
        }
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const getRelativeTime = (date) => {
        const now = new Date();
        const updated = new Date(date);
        const diffMs = now - updated;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} mins ago`;
        if (diffHours < 24) return `${diffHours} hrs ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        return updated.toLocaleDateString();
    };

    const getVideoUrl = (video) => {
        if (!video || !video.url) return '';
        const baseUrl = 'https://uk.backend.hms.hiradentallab.com';
        let videoUrl = video.url;
        if (!videoUrl.startsWith('http')) {
            videoUrl = videoUrl.startsWith('/') ? videoUrl : '/' + videoUrl;
            videoUrl = baseUrl + videoUrl;
        }
        return videoUrl;
    };

    const openVideoInNewTab = (video) => {
        const videoUrl = getVideoUrl(video);
        if (videoUrl) {
            window.open(videoUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleVideoError = (videoId) => {
        setVideoErrors(prev => ({ ...prev, [videoId]: true }));
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
    };

    const filterHeadings = (items, term) => {
        if (!term) return items;
        return items.reduce((acc, item) => {
            const matchesSearch = item.text.toLowerCase().includes(term.toLowerCase());
            const filteredChildren = item.children ? filterHeadings(item.children, term) : [];
            if (matchesSearch || filteredChildren.length > 0) {
                acc.push({ ...item, children: filteredChildren.length > 0 ? filteredChildren : item.children });
            }
            return acc;
        }, []);
    };

    const renderHeadings = (items, depth = 0) => {
        const filteredItems = searchTerm ? filterHeadings(items, searchTerm) : items;

        return filteredItems.map((item) => (
            <li
                key={item.id}
                className={`mp-heading-item ${item.level} ${activeHeading === item.id ? 'mp-active' : ''} ${expandedHeadings[item.id] ? 'mp-expanded' : ''}`}
            >
                <div
                    className="mp-heading-link"
                    onClick={(e) => {
                        e.stopPropagation();
                        scrollToSection(item.id);
                        if (item.children && item.children.length > 0 && !expandedHeadings[item.id]) {
                            toggleHeading(item.id);
                        }
                    }}
                >
                    <span className="mp-heading-indicator" style={{ background: item.color }}></span>
                    <span className="mp-heading-icon-wrapper" style={{ color: item.color }}>
                        {item.icon}
                    </span>
                    <span className="mp-heading-text">{item.text}</span>
                    {item.children && item.children.length > 0 && (
                        <span className="mp-chevron-icon" onClick={(e) => {
                            e.stopPropagation();
                            toggleHeading(item.id);
                        }}>
                            {expandedHeadings[item.id] ? <FaChevronDown /> : <FaChevronRight />}
                        </span>
                    )}
                </div>

                {item.children && item.children.length > 0 && expandedHeadings[item.id] && (
                    <ul className="mp-sub-headings-list">
                        {renderHeadings(item.children, depth + 1)}
                    </ul>
                )}
            </li>
        ));
    };

    const getActiveSectionName = () => {
        if (!activeHeading) return null;
        const info = findHeadingInfo(headingsStructure, activeHeading);
        return info ? info.text : null;
    };

    // Get folder name by ID
    const getFolderName = (folderId) => {
        if (!folderId) return 'Uncategorized';
        const folder = videoFolders.find(f => f._id === folderId);
        return folder ? folder.name : 'Uncategorized';
    };

    // Get videos for a specific folder
    const getVideosByFolder = (folderId) => {
        if (!folderId) return manual?.videos?.filter(v => !v.folderId || v.folderId === null) || [];
        return manual?.videos?.filter(v => v.folderId === folderId) || [];
    };

    // Get all uncategorized videos
    const getUncategorizedVideos = () => {
        return manual?.videos?.filter(v => !v.folderId || v.folderId === null) || [];
    };

    // Open folder modal
    const openFolderModal = (folder) => {
        setSelectedFolder(folder);
        setIsFolderModalOpen(true);
    };

    // Close folder modal
    const closeFolderModal = () => {
        setIsFolderModalOpen(false);
        setSelectedFolder(null);
    };

    const renderVideoCard = (video) => {
        const videoUrl = getVideoUrl(video);
        const hasError = videoErrors[video._id];

        return (
            <div key={video._id} className="mp-video-card">
                <div className="mp-video-thumbnail">
                    {!hasError ? (
                        <video
                            className="mp-video-preview"
                            src={videoUrl}
                            onError={() => handleVideoError(video._id)}
                            preload="metadata"
                        />
                    ) : (
                        <div className="mp-video-error-thumbnail">
                            <FaExclamationTriangle className="mp-error-icon" />
                            <p>Video unavailable</p>
                        </div>
                    )}
                    <div className="mp-video-play-overlay">
                        <FaPlayCircle className="mp-play-icon-large" />
                    </div>
                </div>
                <div className="mp-video-card-content">
                    <h4>{video.title}</h4>
                    {video.description && <p>{video.description}</p>}
                    <div className="mp-video-meta">
                        <div className="mp-meta-item-mini">
                            <FaFileAlt className="mp-mini-icon" />
                            <span>{formatFileSize(video.size)}</span>
                        </div>
                        <div className="mp-meta-item-mini">
                            <FaRegClock className="mp-mini-icon" />
                            <span>Uploaded: {video.uploadedAt ? new Date(video.uploadedAt).toLocaleDateString() : 'N/A'}</span>
                        </div>
                    </div>
                    <button
                        onClick={() => openVideoInNewTab(video)}
                        className="mp-btn-watch-video"
                    >
                        <FaExternalLinkAlt /> Watch Video
                    </button>
                </div>
            </div>
        );
    };

    // Render folder icon with name below (File Explorer style)
    const renderFolderIcon = (folder, videos) => {
        return (
            <div
                key={folder._id}
                className="mp-folder-icon-container"
                onClick={() => openFolderModal(folder)}
                title={`${folder.name} (${videos.length} videos)`}
            >
                <div className="mp-folder-icon-wrapper">
                    <FaFolder className="mp-folder-icon" />
                    {videos.length > 0 && (
                        <span className="mp-folder-video-badge">{videos.length}</span>
                    )}
                </div>
                <span className="mp-folder-name">{folder.name}</span>
                <span className="mp-folder-video-count-text">{videos.length} videos</span>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="mp-manual-page">
                <div className="mp-loading-container">
                    <div className="mp-loading-spinner">
                        <div className="mp-spinner"></div>
                        <p>Loading manual...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mp-manual-page">
                <div className="mp-error-container">
                    <FaExclamationTriangle className="mp-error-icon-large" />
                    <h3>Error Loading Manual</h3>
                    <p>{error}</p>
                    <button onClick={fetchManual} className="mp-retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const activeSectionName = getActiveSectionName();
    const allVideos = manual?.videos || [];
    const uncategorizedVideos = getUncategorizedVideos();

    return (
        <div className="mp-manual-page">
            <div className="mp-manual-layout">
                <button className="mp-mobile-sidebar-toggle" onClick={toggleMobileSidebar}>
                    {mobileSidebarOpen ? <FaTimes /> : <FaBars />}
                </button>

                <aside className={`mp-manual-sidebar ${sidebarOpen ? 'mp-open' : 'mp-closed'} ${mobileSidebarOpen ? 'mp-mobile-open' : ''}`}>
                    <div className="mp-sidebar-header">
                        <div className="mp-sidebar-header-content">
                            <div className="mp-sidebar-logo">
                                <FaLayerGroup className="mp-logo-icon" />
                            </div>
                            <div className="mp-sidebar-title-group">
                                <h3>Contents</h3>
                                <p>Navigation Guide</p>
                            </div>
                        </div>
                        <button className="mp-sidebar-close-btn" onClick={toggleSidebar}>
                            <FaTimes />
                        </button>
                    </div>

                    <div className="mp-sidebar-search">
                        <div className="mp-search-input-wrapper">
                            <FaSearch className="mp-search-icon" />
                            <input
                                type="text"
                                placeholder="Search sections..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="mp-search-input"
                            />
                            {searchTerm && (
                                <button className="mp-search-clear" onClick={() => setSearchTerm('')}>
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    </div>

                    <nav className="mp-sidebar-nav">
                        <div className="mp-nav-section-label">
                            <span>TABLE OF CONTENTS</span>
                        </div>
                        <ul className="mp-headings-list">
                            {renderHeadings(headingsStructure)}
                        </ul>
                    </nav>

                    <div className="mp-sidebar-footer">
                        <div className="mp-sidebar-footer-content">
                            <span>OSHMS</span>
                        </div>
                    </div>
                </aside>

                {mobileSidebarOpen && (
                    <div className="mp-sidebar-overlay" onClick={toggleMobileSidebar}></div>
                )}

                <main className={`mp-manual-main ${sidebarOpen ? '' : 'mp-sidebar-closed'}`}>
                    <div className="mp-manual-container-fluid">
                        <div className="mp-manual-content-wrapper">
                            <div className="mp-manual-header-section">
                                <div className="mp-header-top">
                                    <h2>{manual?.title || 'Digital Workflow & Training'}</h2>
                                    {activeSectionName && (
                                        <div className="mp-current-section-badge">
                                           {activeSectionName}
                                        </div>
                                    )}
                                </div>
                                <div className="mp-manual-meta-grid">
                                    <div className="mp-meta-item">
                                        <FaCalendarAlt className="mp-meta-icon" />
                                        <div>
                                            <span className="mp-meta-label">Last Updated</span>
                                            <span className="mp-meta-value">
                                                {manual?.updatedAt ? new Date(manual.updatedAt).toLocaleDateString('en-PK', {
                                                    day: 'numeric',
                                                    month: 'long',
                                                    year: 'numeric'
                                                }) : 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mp-meta-item">
                                        <FaClock className="mp-meta-icon" />
                                        <div>
                                            <span className="mp-meta-label">Updated</span>
                                            <span className="mp-meta-value">{getRelativeTime(manual?.updatedAt)}</span>
                                        </div>
                                    </div>
                                    <div className="mp-meta-item">
                                        <MdOutlineUpdate className="mp-meta-icon" />
                                        <div>
                                            <span className="mp-meta-label">Status</span>
                                            <span className="mp-meta-value mp-status-badge">Active</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mp-manual-body-section">
                                <div className="mp-content-header">
                                    <MdOutlineDescription className="mp-section-icon" />
                                    <h3>Documentation</h3>
                                </div>

                                {/* Scrollable Documentation Container */}
                                <div className="mp-manual-content-container">
                                    <div
                                        ref={scrollContainerRef}
                                        className={`mp-documentation-scroll-container ${
                                            showTopGradient ? 'mp-scrollable-top' : ''
                                        } ${
                                            showBottomGradient ? 'mp-scrollable-bottom' : ''
                                        }`}
                                    >
                                        <div
                                            ref={contentRef}
                                            className="mp-manual-content"
                                        >
                                            <div
                                                className="mp-tiptap-content"
                                                dangerouslySetInnerHTML={{ __html: manual?.content || '<p>No content available</p>' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Training Videos Section - File Explorer Style */}
                            <div ref={videosSectionRef} className="mp-videos-section" id="training-videos" style={{ display: 'none' }}>
                                {allVideos.length > 0 || videoFolders.length > 0 ? (
                                    <>
                                        <div className="mp-content-header">
                                            <MdVideoLibrary className="mp-section-icon" />
                                            <h3>HMS Archive</h3>
                                        </div>

                                        {/* File Explorer Style Folder Display */}
                                        <div className="mp-file-explorer-container">
                                            {/* Folders Grid */}
                                            <div className="mp-folders-grid">
                                                {/* Uncategorized Videos Folder (if any) */}
                                                {uncategorizedVideos.length > 0 && (
                                                    <div
                                                        className="mp-folder-icon-container"
                                                        onClick={() => openFolderModal({ _id: 'uncategorized', name: 'Uncategorized' })}
                                                        title={`Uncategorized (${uncategorizedVideos.length} videos)`}
                                                    >
                                                        <div className="mp-folder-icon-wrapper">
                                                            <FaFolderOpen className="mp-folder-icon" />
                                                            {uncategorizedVideos.length > 0 && (
                                                                <span className="mp-folder-video-badge">{uncategorizedVideos.length}</span>
                                                            )}
                                                        </div>
                                                        <span className="mp-folder-name">Uncategorized</span>
                                                        <span className="mp-folder-video-count-text">{uncategorizedVideos.length} videos</span>
                                                    </div>
                                                )}

                                                {/* Regular Folders */}
                                                {videoFolders.map(folder => {
                                                    const folderVideos = getVideosByFolder(folder._id);
                                                    return renderFolderIcon(folder, folderVideos);
                                                })}
                                            </div>

                                            {/* Show all videos option if no folders */}
                                            {videoFolders.length === 0 && uncategorizedVideos.length === 0 && (
                                                <div className="mp-videos-grid">
                                                    {allVideos.map(video => renderVideoCard(video))}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="mp-content-header">
                                            <FaVideo className="mp-section-icon" />
                                            <h3>Training Videos</h3>
                                        </div>
                                        <div className="mp-no-videos-message">
                                            <p>No training videos available yet.</p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="mp-manual-footer-section">
                                <div className="mp-footer-note">
                                    <p>📌 This Manual is regularly updated.</p>
                                </div>
                                <div className="mp-footer-meta">
                                    <span>Generated: {new Date().toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Folder Modal */}
            {isFolderModalOpen && selectedFolder && (
                <div className="mp-folder-modal-overlay" onClick={closeFolderModal}>
                    <div className="mp-folder-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="mp-folder-modal-header">
                            <div className="mp-folder-modal-title">
                                <FaFolder className="mp-folder-modal-icon" />
                                <h3>{selectedFolder.name}</h3>
                                <span className="mp-folder-modal-count">
                                    {getVideosByFolder(selectedFolder._id === 'uncategorized' ? null : selectedFolder._id).length} videos
                                </span>
                            </div>
                            <button className="mp-folder-modal-close" onClick={closeFolderModal}>
                                <FaTimesCircle />
                            </button>
                        </div>
                        <div className="mp-folder-modal-body">
                            {getVideosByFolder(selectedFolder._id === 'uncategorized' ? null : selectedFolder._id).length > 0 ? (
                                <div className="mp-videos-grid">
                                    {getVideosByFolder(selectedFolder._id === 'uncategorized' ? null : selectedFolder._id).map(video => renderVideoCard(video))}
                                </div>
                            ) : (
                                <div className="mp-no-videos-message">
                                    <p>No videos in this folder.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManualsPage;